import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  adminAddAssignedUser,
  adminDeleteAssignedUser,
  adminGetRoles,
  adminUpdateAssignedUser,
  getAdminAssignedUsers,
  getAllAdminUsers,
  getUnAssignedRoles,
} from "./roleAndPermissionActions";
import RoleAndPermissionStyles from "./RoleAndPermissionStyles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { getAllAdminRoles } from "../UserManagement/UserManagementActions";

const AssignedUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { adminRoleData } = useAppSelector(
    (state: RootState) => state.adminRoles
  );
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (state) => state.common
  );
  const { assignedUsersData, allAdminUsersData, unAssignedRoles } =
    useAppSelector((state: RootState) => state.adminRoles);
  const roles = adminRoleData.rolePermissionsResponses;

  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  // Add these states for user selection
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // For Change Role modal
  const [openChangeRoleDialog, setOpenChangeRoleDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState<any>(null);
  const [selectedNewRoles, setSelectedNewRoles] = useState<string[]>([]);

  // Add new state for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  // 1. Compute the list of users NOT assigned to the selected role:
  const assignedEmails = useMemo(
    () => (assignedUsersData || [])?.map((user) => user.email),
    [assignedUsersData]
  );

  const unassignedUsers = useMemo(
    () =>
      (allAdminUsersData || [])?.filter(
        (user: any) => !assignedEmails.includes(user)
      ),
    [allAdminUsersData, assignedEmails]
  );

  useEffect(() => {
    if (selectedRole.name) {
      dispatch(getAdminAssignedUsers(selectedRole.name));
    }
  }, [selectedRole]);

  useEffect(() => {
    if (openAddUserDialog) {
      setAllUsers(allAdminUsersData);
    }
  }, [openAddUserDialog]);

  useEffect(() => {
    dispatch(getAllAdminUsers());
  }, []);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      (calledReducerType === "roles/adminAddAssignedUser" ||
        calledReducerType === "roles/adminDeleteAssignedUser" ||
        calledReducerType === "roles/adminUpdateAssignedUser")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  useEffect(() => {
    dispatch(getAllAdminRoles());
  }, [dispatch]);

  const filteredUsers = useMemo(
    () =>
      (assignedUsersData || [])?.filter(
        (user) =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase())
      ),
    [search, assignedUsersData]
  );

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    dispatch(getAdminAssignedUsers(selectedRole.name));
    dispatch(adminGetRoles());
  };
  // Handlers for edit/delete (implement as needed)
  const handleEditUser = (user: any) => {
    dispatch(getUnAssignedRoles(user?.email));
    setUserToEdit(user);
    setSelectedNewRoles([]);
    setOpenChangeRoleDialog(true);
  };
  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    dispatch(
      adminDeleteAssignedUser({
        user: userToDelete.email,
        role: selectedRole.name,
      })
    );
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleAddUsers = () => {
    const payload = {
      role: selectedRole.name,
      emails: selectedUsers,
    };
    dispatch(adminAddAssignedUser(payload));
    setOpenAddUserDialog(false);
    setSelectedUsers([]);
  };

  const handleChangeRole = () => {
    if (!userToEdit || selectedNewRoles.length === 0) return;
    const payload = {
      user: userToEdit?.email,
      addRoles: selectedNewRoles,
      currentRole: selectedRole?.name,
    };
    dispatch(adminUpdateAssignedUser(payload));
    setOpenChangeRoleDialog(false);
    setUserToEdit(null);
    setSelectedNewRoles([]);
  };

  return (
    <>
      <Box>
        <Grid
          container
          spacing={2}
          sx={RoleAndPermissionStyles.cardGrid}
          id="assignedroles_grid"
          data-test-id="test-assignedroles_grid"
          aria-label="Assigned Roles Grid"
        >
          {roles?.map((role) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={role.name}
              id={`assignedrole_item_${role.name}`}
              data-test-id={`test-assignedrole_item_${role.name}`}
              aria-label={`Assigned Role Item ${role.name}`}
            >
              <Paper
                onClick={() => setSelectedRole(role)}
                sx={RoleAndPermissionStyles.roleCard(
                  selectedRole.name === role.name
                )}
                id={`assignedrole_card_${role.name}`}
                data-test-id={`test-assignedrole_card_${role.name}`}
                aria-label={`Assigned Role Card ${role.name}`}
              >
                <Typography
                  sx={RoleAndPermissionStyles.roleName}
                  id={`assignedrole_name_${role.name}`}
                  data-test-id={`test-assignedrole_name_${role.name}`}
                  aria-label={`Role Name ${role.name}`}
                >
                  {role.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={RoleAndPermissionStyles.assignedUsersText}
                  id={`assignedrole_users_${role.name}`}
                  data-test-id={`test-assignedrole_users_${role.name}`}
                  aria-label={`${role.totalUsersPerRole} Assigned Users`}
                >
                  {role.totalUsersPerRole} Assigned Users
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={RoleAndPermissionStyles.headerBox}
          id="assignedusers_headerbox"
          data-test-id="test-assignedusers_headerbox"
          aria-label="Assigned Users Header Box"
        >
          <Typography
            variant="h6"
            sx={RoleAndPermissionStyles.usersTitle}
            id="assignedusers_title"
            data-test-id="test-assignedusers_title"
            aria-label={`Users assigned to ${selectedRole?.name}`}
          >
            Users assigned to {selectedRole?.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              placeholder="Search users..."
              size="small"
              sx={RoleAndPermissionStyles.searchField}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              id="search_users_input"
              data-test-id="test-search_users_input"
              aria-label="Search Users Input"
            />
            <Button
              variant="contained"
              onClick={() => setOpenAddUserDialog(true)}
              sx={{ borderRadius: "4px" }}
              id="add_user_button"
              data-test-id="test-add_user_button"
              aria-label="Add User"
            >
              + Add User
            </Button>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          id="assignedusers_table_container"
          data-test-id="test-assignedusers_table_container"
          aria-label="Assigned Users Table Container"
        >
          <Table
            id="assignedusers_table"
            data-test-id="test-assignedusers_table"
            aria-label="Assigned Users Table"
          >
            <TableHead
              id="assignedusers_table_head"
              data-test-id="test-assignedusers_table_head"
              aria-label="Assigned Users Table Head"
            >
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              id="assignedusers_table_body"
              data-test-id="test-assignedusers_table_body"
              aria-label="Assigned Users Table Body"
            >
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress
                      size={24}
                      id="loading_spinner"
                      data-test-id="test-loading_spinner"
                      aria-label="Loading Spinner"
                    />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    id="no_users_found"
                    data-test-id="test-no_users_found"
                    aria-label="No Users Found"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers?.map((user, idx) => (
                  <TableRow
                    key={idx}
                    id={`user_row_${idx}`}
                    data-test-id={`test-user_row_${idx}`}
                    aria-label={`User Row ${idx}`}
                  >
                    <TableCell
                      id={`user_name_${idx}`}
                      data-test-id={`test-user_name_${idx}`}
                      aria-label={`User Name ${user.name}`}
                    >
                      {user.name}
                    </TableCell>
                    <TableCell
                      id={`user_email_${idx}`}
                      data-test-id={`test-user_email_${idx}`}
                      aria-label={`User Email ${user.email}`}
                    >
                      {user.email}
                    </TableCell>
                    <TableCell
                      id={`user_created_${idx}`}
                      data-test-id={`test-user_created_${idx}`}
                      aria-label={`User Created Date ${
                        user.creationTime
                          ? new Date(user.creationTime).toLocaleDateString()
                          : "Unknown"
                      }`}
                    >
                      {user.creationTime
                        ? new Date(user.creationTime).toLocaleDateString()
                        : ""}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Change user's assigned role">
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          aria-label="Edit user"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove user from role">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user)}
                          aria-label="Delete user"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Change Role Dialog */}
        <Dialog
          open={openChangeRoleDialog}
          onClose={() => setOpenChangeRoleDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Change Role for {userToEdit?.name}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1, mt: 1 }}>Select New Role</Typography>
            <Autocomplete
              multiple
              options={unAssignedRoles?.filter(
                (role) => role !== selectedRole?.name
              )}
              value={selectedNewRoles}
              onChange={(_, value) => setSelectedNewRoles(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select new role"
                  variant="outlined"
                />
              )}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Current role: {selectedRole?.name}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenChangeRoleDialog(false)}
              sx={{ borderRadius: "4px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={selectedNewRoles.length === 0}
              onClick={handleChangeRole}
              sx={{ borderRadius: "4px" }}
            >
              Change Role
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add User Dialog (simple example) */}
        <Dialog
          open={openAddUserDialog}
          onClose={() => setOpenAddUserDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Add User to {selectedRole.name}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1, mt: 1 }}>Select User</Typography>
            <Autocomplete
              multiple
              options={unassignedUsers}
              value={selectedUsers}
              onChange={(_, value) => setSelectedUsers(value)}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search users..."
                  variant="outlined"
                />
              )}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenAddUserDialog(false);
                setSelectedUsers([]);
              }}
              sx={{ borderRadius: "4px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: "4px" }}
              disabled={selectedUsers.length === 0}
              onClick={handleAddUsers}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm Removal</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove <b>{userToDelete?.name}</b> from{" "}
              <b>{selectedRole.name}</b>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: "4px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              sx={{ borderRadius: "4px" }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default AssignedUsers;
